import moment, { Moment } from 'moment';

import {
  ChatChannelState,
  InterviewChatGranularity,
  InterviewChatNewChatsPoint,
  InterviewChatSummary,
  InterviewStatus,
} from 'api/interviewChats/types';

/**
 * GetStream returns at most 30 channels per query and no total, so each page asks for one
 * row more than it shows: that row is the only proof a next page exists. 25 + 1 stays
 * under the cap.
 */
export const DEFAULT_PAGE_SIZE = 25;
export const SEARCH_DEBOUNCE_MS = 350;
export const TRANSCRIPT_PAGE_SIZE = 50;

/** Above this width the transcript sits beside the table; below it opens in a drawer. */
export const SPLIT_VIEW_MIN_WIDTH = 1400;

export type DateRange = [Moment | null, Moment | null] | null;

export const EMPTY_SUMMARY: InterviewChatSummary = {
  provisioned: 0,
  activated: 0,
  activeLast7Days: 0,
  activeLast30Days: 0,
  activationRatePct: null,
  byState: [],
  newChatsByDay: [],
  scannedChats: 0,
  truncated: false,
  scanFailed: false,
  generatedAt: '',
};

/**
 * The only two an interview chat ever reaches. GetStream exposes the lifecycle as its
 * queryable `frozen` flag, which fs-backend keeps in sync with chat_channel.state.
 */
export const STATE_OPTIONS: { value: ChatChannelState; label: string }[] = [
  { value: 'Active', label: 'Active' },
  { value: 'Frozen', label: 'Frozen' },
];

/** antd Tag colours per lifecycle state. */
export const STATE_COLORS: Record<ChatChannelState, string> = {
  Active: 'green',
  Frozen: 'blue',
  Archived: 'default',
  Deleted: 'red',
};

export const INTERVIEW_STATUS_COLORS: Record<InterviewStatus, string> = {
  Scheduled: 'blue',
  Completed: 'green',
  Canceled: 'red',
  RescheduleRequested: 'orange',
  ScheduleAttempted: 'gold',
  Invited: 'default',
  InviteViewed: 'default',
  Draft: 'default',
};

/** Only chats provisioned per bucket: message volume needs per-message data we no longer pull. */
export const NEW_CHATS_STROKE = '#16a34a';

export function formatRatePct(value: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value.toFixed(1)}%`;
}

export function formatRelative(iso: string | null): string {
  if (!iso) return 'Never';
  return moment(iso).fromNow();
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return moment(iso).format('MMM D, YYYY HH:mm');
}

export function formatBucketLabel(
  iso: string,
  granularity: InterviewChatGranularity
): string {
  const date = moment.utc(iso);
  return granularity === 'Week'
    ? `w/c ${date.format('MMM D')}`
    : date.format('MMM D');
}

/** Groups transcript messages into day buckets so the thread can show date separators. */
export function groupByDay<T extends { createdAt: string }>(
  messages: T[]
): { day: string; label: string; messages: T[] }[] {
  const groups: { day: string; label: string; messages: T[] }[] = [];

  messages.forEach((message) => {
    const date = moment(message.createdAt);
    const day = date.format('YYYY-MM-DD');
    const last = groups[groups.length - 1];

    if (last && last.day === day) {
      last.messages.push(message);
      return;
    }

    const label = date.isSame(moment(), 'day')
      ? 'Today'
      : date.isSame(moment().subtract(1, 'day'), 'day')
      ? 'Yesterday'
      : date.format('dddd, MMM D, YYYY');

    groups.push({ day, label, messages: [message] });
  });

  return groups;
}

/**
 * The summary always returns daily buckets, so the Day/Week toggle is a display choice the
 * browser makes rather than a reason to call the endpoint again.
 */
export function rollUpNewChats(
  points: InterviewChatNewChatsPoint[],
  granularity: InterviewChatGranularity
): { label: string; 'New chats': number }[] {
  const buckets = new Map<string, number>();

  points.forEach((point) => {
    const date = moment.utc(point.bucketStart);
    const start =
      granularity === 'Week' ? date.clone().startOf('isoWeek') : date;
    const key = start.toISOString();

    buckets.set(key, (buckets.get(key) ?? 0) + point.newChats);
  });

  return Array.from(buckets.keys())
    .sort()
    .map((iso) => ({
      label: formatBucketLabel(iso, granularity),
      'New chats': buckets.get(iso) ?? 0,
    }));
}

export function initialsOf(name: string | null): string {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

/**
 * Gates a state setter on the load still being the current one. `runLoad` reports the
 * outcome of whichever load it was given, so without this a slow failure on the chat (or
 * cohort) the user just navigated away from would raise its error, and clear its spinner,
 * over the one now on screen.
 */
export function onlyIfCurrent<T>(
  set: (value: T) => void,
  isCurrent: () => boolean
): (value: T) => void {
  return (value: T) => {
    if (isCurrent()) set(value);
  };
}

// Wraps a loader so each one doesn't repeat the setLoading/try/catch/setError plumbing.
export async function runLoad<T>(
  setLoading: (loading: boolean) => void,
  setError: (message: string | null) => void,
  fallbackMessage: string,
  fn: () => Promise<T>
): Promise<T | undefined> {
  setLoading(true);
  try {
    return await fn();
  } catch (e: any) {
    setError(e?.message || fallbackMessage);
    return undefined;
  } finally {
    setLoading(false);
  }
}
